/**
 * Map pixel-space row segments to SVG rectangles in a viewBox (uniform scale, centered).
 */

/**
 * @param {{ rowIndex: number, segments: { x0: number, x1: number }[] }[]} rows
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bbox
 * @param {{ width: number, height: number }} viewBox
 * @param {object} [options]
 * @param {number} [options.padding=4]
 * @param {number} [options.barFillRatio=0.42] - bar height as fraction of row pitch
 * @param {number} [options.opacity=1]
 * @returns {{ x: number, y: number, w: number, h: number, rx: number, opacity: number }[]}
 */
export function mapSegmentsToViewBox(rows, bbox, viewBox, options = {}) {
  const padding = options.padding ?? 4;
  const barFillRatio = options.barFillRatio ?? 0.42;
  const opacity = options.opacity ?? 1;
  const { width: vbW, height: vbH } = viewBox;
  const { minX, minY, maxX, maxY } = bbox;

  const contentW = maxX - minX;
  const contentH = maxY - minY;
  const innerW = vbW - 2 * padding;
  const innerH = vbH - 2 * padding;
  const scale = Math.min(innerW / contentW, innerH / contentH);
  const drawnW = contentW * scale;
  const drawnH = contentH * scale;
  const originX = (vbW - drawnW) / 2;
  const originY = (vbH - drawnH) / 2;
  const rowPitch = scale;
  const barH = rowPitch * barFillRatio;

  const rects = [];

  for (const row of rows) {
    if (row.rowIndex < minY || row.rowIndex >= maxY) continue;
    const ySvg = originY + (row.rowIndex - minY) * rowPitch + (rowPitch - barH) / 2;
    for (const seg of row.segments) {
      const xSvg = originX + (seg.x0 - minX) * scale;
      const wSvg = (seg.x1 - seg.x0) * scale;
      const rx = Math.min(barH / 2, wSvg / 2);
      rects.push({ x: xSvg, y: ySvg, w: wSvg, h: barH, rx, opacity });
    }
  }

  return rects;
}
